import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import { CommonModule, NgIf, NgForOf, NgTemplateOutlet, AsyncPipe, DatePipe } from '@angular/common'
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms'
import { IonicModule, AlertController, IonLabel, ModalController } from '@ionic/angular'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { Assistance } from 'src/app/models/assistance'
import { DateTimePickerComponent } from '@syncfusion/ej2-angular-calendars'
import { AssistanceStatusManagerService } from 'src/app/services/assistance-status-manager.service'
import {
  HtmlEditorService,
  ImageService,
  LinkService,
  PasteCleanupService,
  PasteCleanupSettingsModel,
  RichTextEditorComponent,
  ToolbarService,
} from '@syncfusion/ej2-angular-richtexteditor'
import { RecurrenceEditorChangeEventArgs } from '@syncfusion/ej2-schedule'
import { AssistanceService } from '../../../services/domain/assistance/assistance.service'
import { RecurrenceEditingMode } from '../../recurrence/recurrenceEditing'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { firstValueFrom, Observable, Subject } from 'rxjs'
import {
  isLateOrderedAndRecurrentAsyncValidator,
  isOverlappingAnyOtherAssistanceForTheSameAssistantAsyncValidator,
  isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator,
  isPlannedDateTimeSoonerThanConfigAllowsAsyncValidator,
  plannedDatetimeToLaterThanFromValidator,
  realizedDatetimeToLaterThanFromValidator,
  realizedToOrFromOnlyValidator,
  recurrenceUntilLaterThan1970,
  recurrenceUntilLaterThanPlannedFromValidator,
  recurrentAssistanceHasAtLeastOneOccurrence,
  requiredDateTimeValidator,
  validateRequiredFieldsWhenFormDisabled,
} from './assistance-editor-validators'
import { ejsDatetimepickerInit } from '../../../shared/configs/ejs-datetimepicker.config'
import { RecurrenceEditorShowStatus } from './RecurrenceEditorShowStatus'
import { UsersService } from '../../../services/domain/users.service'
import { ConfigService } from '../../../services/config.service'
import { StateAtLeastOneTrue } from '../../../shared/state/StateAtLeastOneTrue'
import { AssistanceEditorModalService } from '../assistance-editor-modal/assistance-editor-modal.service'
import { FormStatusType } from '../../../models/functional/formStatusType'
import { SimpleStash } from '../../stash/SimpleStash'
import { RtePasteCleanupSettings } from '../../../shared/configs/ejsRteSettings'
import { FormsUtilsService } from '../../../services/formsUtils'
import { AssistanceJournalEntry, EditorAssistance, EditorAssistanceAssignment } from '../../../models/EditorAssistance'
import { CareLogEditorAssistant } from '../../../models/assistance-care-log.model'
import { AssistanceCareLogEditorComponent } from '../assistance-care-log-editor/assistance-care-log-editor.component'
import { AssistanceEditorService } from './assistance-editor.service'
import { AssistanceStatusesService } from '../../../services/domain/codelists/assistance-statuses.service'
import { AssistanceEditorForm, getDefaultValues, mapFormFieldsToAssistance, updateForm } from './AssistanceEditorForm'
import { AssistantService } from '../../../services/domain/assistant/assistant.service'
import { ClientService } from '../../../services/domain/client/client.service'
import { AssignmentsManagerService } from '../../assignments/assignments-manager.service'
import { EditorAssistanceFormValueChangeHandler } from './EditorAssistanceFormValueChangeHandler'
import { FeatureFlags } from '../../../shared/featureFlags'
import { debounce } from '../../../shared/helpers/debounce'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { DateRange } from '../../../services/date-utils.service'
import { DateTime } from '../../../models/DateTime/DateTime'
import { InitializableMap } from '../../initializable-map/InitializableMap'
import { HumanizeRecurrenceEditingModePipe } from '../../recurrence/pipes/humanize-recurrence-editing-mode.pipe'
import { AssistanceEditorModalEditableHandler } from '../assistance-editor-modal/AssistanceEditorModalEditableHandler'
import { log } from '../../../shared/utils/rxjsUtils'
import { LOADING_ID, LoadingService } from '../../loading/loading.service'
import { ToasterService } from '../../notification/toaster.service'
import { AssistanceHooks } from '../../../feathersHooks/assistance.hooks'
import { GendersService } from '../../../services/domain/codelists/genders.service'
import { Gender } from '../../../models/gender'
import {
  IonicSelectableComponent,
  IonicSelectableHeaderTemplateDirective,
  IonicSelectableItemTemplateDirective,
  IonicSelectableValueTemplateDirective,
} from 'ionic-selectable'
import { DatePickerModule, DateTimePickerModule, TimePickerModule } from '@syncfusion/ej2-angular-calendars'
import { RichTextEditorModule } from '@syncfusion/ej2-angular-richtexteditor'
import { RecurrenceEditorModule } from '@syncfusion/ej2-angular-schedule'
import { AssistantSelectComponent } from '../../assistant/assistant-select/components/assistant-select/assistant-select.component'
import { BypassHtmlSanitizerPipe } from '../../../shared/pipes/bypass-html-sanitizer.pipe'
import { AssistanceEditorAssistanceStatusComponent } from './assistance-editor-assistance-status/assistance-editor-assistance-status.component'
import { GenderSelectComponent } from './gender-select/gender-select.component'
import { ClientSelectComponent } from './client-select/client-select.component'
import { KeyCoordinatorSelectComponent } from './key-coordinator-select/key-coordinator-select.component'
import { AssistanceEditorRecurrenceEditorComponent } from './assistance-editor-recurrence-editor/assistance-editor-recurrence-editor.component'
import { AssistanceEditorAssistantSelectComponent } from './assistance-editor-assistant-select/assistance-editor-assistant-select.component'
import { AssistanceEditorFinanceViewerComponent } from './assistance-editor-finance-viewer/assistance-editor-finance-viewer.component'
import { DateTimeInputComponent } from '../../../components/date-time-input/date-time-input.component'
import { AssistanceEditorJournalEntriesComponent } from './assistance-editor-journal-entries/assistance-editor-journal-entries.component'
import { AssistanceEditorSavePopupComponent } from './assistance-editor-save-popup/assistance-editor-save-popup.component'
import { TranslocoModule } from '@jsverse/transloco'

@Component({
  selector: 'app-assistance-editor',
  templateUrl: './assistance-editor.component.html',
  styleUrls: ['./assistance-editor.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ToolbarService, LinkService, ImageService, HtmlEditorService, PasteCleanupService, HumanizeRecurrenceEditingModePipe],
  imports: [
    CommonModule,
    IonicModule,
    TranslocoModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DatePickerModule,
    TimePickerModule,
    DateTimePickerModule,
    RichTextEditorModule,
    RecurrenceEditorModule,
    BypassHtmlSanitizerPipe,
    DateTimeInputComponent,
    ClientSelectComponent,
    KeyCoordinatorSelectComponent,
    AssistanceEditorAssistanceStatusComponent,
    AssistanceEditorRecurrenceEditorComponent,
    AssistanceEditorAssistantSelectComponent,
    AssistanceEditorFinanceViewerComponent,
    AssistanceEditorJournalEntriesComponent,
    AssistanceCareLogEditorComponent,
    NgIf,
    NgForOf,
    AsyncPipe,
    DatePipe,
  ],
})
export class AssistanceEditorComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private fb = inject(FormBuilder)
  private alertCtrl = inject(AlertController)
  private assignmentManager = inject(AssignmentsManagerService)
  private assistanceService = inject(AssistanceService)
  private usersService = inject(UsersService)
  private configService = inject(ConfigService)
  assistanceEditorModalService = inject(AssistanceEditorModalService)
  private assistanceEditorService = inject(AssistanceEditorService)
  private modalCtrl = inject(ModalController)
  statusService = inject(AssistanceStatusesService)
  private assistantService = inject(AssistantService)
  clientsService = inject(ClientService)
  private assistanceStatusManager = inject(AssistanceStatusManagerService)
  private humanizeRecurrenceEditingModePipe = inject(HumanizeRecurrenceEditingModePipe)
  private loadingService = inject(LoadingService)
  private viewContainerRef = inject(ViewContainerRef)
  private toaster = inject(ToasterService)
  private gendersService = inject(GendersService)
  private cdr = inject(ChangeDetectorRef)

  @Input() assistance: EditorAssistance
  @Input() recurrenceEditingMode: RecurrenceEditingMode
  @Input() isInBundle: boolean = false
  @Input() editableHandler: AssistanceEditorModalEditableHandler

  @Output() bundleChildAssistanceDeleted = new EventEmitter<number>()
  @Output() recurrenceEditingModeChange = new EventEmitter<RecurrenceEditingMode>()
  @Output() notifyURIDChange = new EventEmitter<string>()

  @ViewChild('assistantSelect') assistantSelect: AssistantSelectComponent
  @ViewChild('detailedDescriptionRTE')
  detailedDescriptionRTE: RichTextEditorComponent
  @ViewChild('realizedFrom') realizedFromPicker: DateTimePickerComponent
  @ViewChild('realizedTo') realizedToPicker: DateTimePickerComponent
  isWarningForRealizedDatesCache: boolean = false

  //Labels
  @ViewChild('addressStartLabel') addressStart: IonLabel
  @ViewChild('addressEndLabel') addressEnd: IonLabel
  @ViewChild('plannedFromLabel') plannedFrom: IonLabel
  @ViewChild('plannedToLabel') plannedTo: IonLabel
  @ViewChild('keyCoordinatorIdLabel') keyCoordinatorId: IonLabel

  private unsubscribe$$ = new Subject<void>()
  unsubscribe$ = this.unsubscribe$$.asObservable()

  private _mappedAssignments: CareLogEditorAssistant[] = []

  RecurrenceEditingMode = RecurrenceEditingMode
  form: AssistanceEditorForm

  // Format: assistanceId => Map(stashName => SimpleStash)
  formStashes = new InitializableMap<number, InitializableMap<string, SimpleStash>>()

  public richTextEditorToolbarSettings: object = {
    type: 'MultiRow',
    enableFloating: false,
    items: [
      'Undo',
      'Redo',
      '|',
      'Bold',
      'Italic',
      'Underline',
      'StrikeThrough',
      '|',
      'FontName',
      'FontSize',
      'FontColor',
      'BackgroundColor',
      '|',
      'SubScript',
      'SuperScript',
      '|',
      'LowerCase',
      'UpperCase',
      '|',
      'Formats',
      'Alignments',
      '|',
      'OrderedList',
      'UnorderedList',
      '|',
      'Indent',
      'Outdent',
      '|',
      'CreateLink',
      '|',
      'ClearFormat',
    ],
  }
  public rtePasteCleanupSettings: PasteCleanupSettingsModel = RtePasteCleanupSettings

  recurrenceEditorShowManager: RecurrenceEditorShowStatus = new RecurrenceEditorShowStatus()
  private TAG = 'AssistanceEditor'
  private recurrenceEditorWasClickedAtLeastOnce: boolean = false
  private forceDisableAssistantField: StateAtLeastOneTrue = new StateAtLeastOneTrue(false)
  private recurrenceEditorChange = new Subject<RecurrenceEditorChangeEventArgs>()
  private formInitialized = false
  public currentUser$ = this.usersService.getCurrentUser()
  isPopoverOpen: boolean = false
  private statusAllowsDeletion: boolean = false
  changeHandler: EditorAssistanceFormValueChangeHandler
  private isSplitSeries = false
  protected debouncedSave = debounce(this.doSubmit.bind(this), 300)
  protected debouncedSaveWithPopup = debounce(this.saveWithPopup.bind(this), 300)
  protected realizedValidationErrorsMessages: string[] = []
  private readonly realizedFromToErrorMessages = {
    validRealizedDatetimeToLaterThanFrom: 'Reálný konec asistence nesmí být dříve než začátek.',
    realizedFromPartial: 'Čas začátku asistence je vyplněn neúplně.',
    realizedToPartial: 'Čas konce asistence je vyplněn neúplně.',
    realizedFromToPartial: 'Reálné začátek a konec asistence jsou vyplněné neúplně.',
  }
  areRealizedDatesValid: boolean = false
  protected editable: boolean
  genders$: Observable<Gender[]>
  private initialJournalEntries: AssistanceJournalEntry[] = []

  private clientId: number
  private readonly destroy$: Subject<void> = new Subject<void>()

  /**
   * Returns current clientId from form or assistance
   * Used by tag editor for empty state link
   */
  get currentClientId(): number | null {
    return this.form?.get('clientId')?.value || this.assistance?.clientId || null
  }

  get mappedAssignments(): CareLogEditorAssistant[] {
    return this._mappedAssignments
  }

  getJournalEntryContentForView(entries: AssistanceJournalEntry[], assistantId: number): string {
    return entries?.find((e) => e.assistantId === assistantId)?.content ?? ''
  }

  constructor() {
    console.log('AssistanceEditorComponent#constructor')
    ejsDatetimepickerInit()
  }

  async ngOnInit() {
    console.log(this.TAG, 'onInit', this.assistance)
    this.genders$ = this.gendersService.getAll()
  }

  ngAfterViewInit(): void {
    this.loadingService.setViewContainerRef(this.viewContainerRef)
  }

  async ngOnChanges(changes: SimpleChanges) {
    console.log('🟠 [AssistanceEditor] ngOnChanges called with props:', Object.keys(changes))

    let shouldInitAll = false

    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        if (propName === 'editableHandler') {
          this.editableHandler
            .getEditable()
            .pipe(takeUntil(this.unsubscribe$), log(`${this.TAG}#ngOnChanges editableStateHandler`))
            .subscribe((editable) => (this.editable = editable))
        }

        if (propName === 'recurrenceEditingMode') {
          console.log(this.TAG, 'showRecurrenceEditorChange', this.recurrenceEditingMode)
          if (this.recurrenceEditingMode === RecurrenceEditingMode.currentOccurrence) {
            this.recurrenceEditorShowManager.setReason('seriesEditing', false)
          }
          if (this.recurrenceEditingMode === RecurrenceEditingMode.currentOccurrenceAndFollowing) {
            this.recurrenceEditorShowManager.setReason('seriesEditing', true)
          }
          this.isSplitSeries = this.recurrenceEditingMode === RecurrenceEditingMode.currentOccurrenceAndFollowing
        }

        if (propName === 'assistance') {
          if (this.assistance?.journalEntries?.length) {
            await this.enrichJournalEntriesForDisplay(this.assistance)
          }
          const assignments = this.assistance?.assignments ?? []
          this._mappedAssignments = assignments.map((asn) => ({
            assignmentId: asn.id,
            assistantId: asn.assistantId,
            fullname: asn.fullname,
          }))
          this.cdr.markForCheck()
          await this.editableHandler.calculateEditable(this.assistance)
          if (this.form && this.assistanceEditorModalService.formStatus.isSettled()) {
            // When an update to loaded assistance comes in, we want to mark the form as pristine only if the form has no pending changes.
            this.form.markAsPristine()
          }
          if (!this.formStashes.get(+this.assistance.id)) {
            const assistanceStashes = new InitializableMap<string, SimpleStash>()
            assistanceStashes.set(
              'plannedFrom',
              new SimpleStash<DateTime>(DateTime.fromDate(this.assistance.plannedFrom)),
            )
            this.formStashes.set(+this.assistance.id, assistanceStashes)
          }
        }

        if (['assistance', 'isInBundle'].includes(propName)) {
          // TODO: cleanup
          if (this.assistance) {
            shouldInitAll = true

            this.recurrenceEditorShowManager.setReason('disableForBundles', this.isInBundle)
            // this.recurrenceEditorShowManager.setReason('hasRecurrenceParent', !!this.assistance.recurrenceParentId) // TODO should this be here? What about when we just change expanded A to exception?

            this.statusAllowsDeletion = await this.assistanceEditorService.canDeleteAssistance(this.assistance.statusId)

            await this.editableHandler.calculateEditable(this.assistance)
          }
        }
      }
    }

    console.log(this.TAG, 'shouldInitAll', shouldInitAll)
    if (shouldInitAll) await this.initAll()
  }

  private async initAll() {
    if (!this.assistance.id && !this.assistance.recurrenceParentId)
      this.changeRecurrenceEditingMode(RecurrenceEditingMode.none)
    this.recurrenceEditorWasClickedAtLeastOnce = false

    try {
      await this.loadingService.show(LOADING_ID.ASSISTANCE_LOAD, 'app-assistance-editor')
      if (!this.formInitialized) {
        this.formInitialized = true
        await this.initForm()
      } else {
        const recordClient = await firstValueFrom(this.clientsService.getById(this.assistance.clientId))
        updateForm(this.form, this.assistance, recordClient)
        this.changeHandler.handleInitial()
      }
    } catch (err) {
      await this.toaster.simpleToast('Nepodařilo se načíst asistenci')
      console.error('Nepodařilo se načíst asistenci', err)
    } finally {
      await this.loadingService.hide([{ tagName: 'app-assistance-editor', message: LOADING_ID.ASSISTANCE_LOAD }])
    }

    this.isWarningForRealizedDatesCache = this.isWarningForRealizedDates()
    // Set initial clientId if available (for tag editor)
    const initialClientId = this.form.get('clientId')?.value
    if (initialClientId) {
      this.clientId = initialClientId
    }
  }

  private changeRecurrenceEditingMode(mode: RecurrenceEditingMode) {
    this.recurrenceEditingModeChange.emit(mode)
  }

  async initForm() {
    console.log(this.TAG, 'initForm', this.assistance, this.recurrenceEditingMode)

    // TODO: why this?
    if (this.form) this.assistance.detailedDescription = this.form.get('detailedDescription').value

    // TODO: getting rid of recurrenceEditingMode?
    if (this.recurrenceEditingMode === RecurrenceEditingMode.currentOccurrence) {
      this.assistance.rrule = null
      this.assistance.recurrenceException = null
    }

    const defaultValues = await getDefaultValues(this.assistance, this.clientsService, this.statusService)

    console.log(this.TAG, 'defaultValues', defaultValues)

    this.form = this.fb.group(
      {
        urid: defaultValues.urid,

        clientId: [defaultValues.clientId, Validators.required],
        statusId: [defaultValues.statusId, Validators.required],
        keyCoordinatorId: [defaultValues.keyCoordinatorId, Validators.required],

        plannedFrom: [defaultValues.plannedFrom, requiredDateTimeValidator],
        plannedTo: [defaultValues.plannedTo, requiredDateTimeValidator],

        realizedFrom: [defaultValues.realizedFrom],
        realizedTo: [defaultValues.realizedTo],

        addressStart: [defaultValues.addressStart, Validators.required],
        addressEnd: [defaultValues.addressEnd, Validators.required],

        assistantAssignments: [defaultValues.assistantAssignments],

        isSentToClient: [defaultValues.isSentToClient],
        coordinatorNote: [defaultValues.coordinatorNote],
        shortDescription: [defaultValues.shortDescription],
        detailedDescription: [defaultValues.detailedDescription],
        isPaidInternally: [defaultValues.isPaidInternally],
        isNotToBePrimarilyAssigned: [defaultValues.isNotToBePrimarilyAssigned],
        isLateOrder: [defaultValues.isLateOrder],
        preferredAssistantGenderId: [defaultValues.preferredAssistantGenderId],
        strongAssistantNeeded: [defaultValues.strongAssistantNeeded],
        advancedAssistantNeeded: [defaultValues.advancedAssistantNeeded],
        isSleepover: [defaultValues.isSleepover],
        nonClientWork: [defaultValues.nonClientWork],
        isShowInExchange: [defaultValues.isShowInExchange],
        journalEntries: [defaultValues.journalEntries],

        rrule: [defaultValues.rrule],
      },
      {
        validators: [
          // PlannedDateTimePastValidator,
          plannedDatetimeToLaterThanFromValidator,
          realizedDatetimeToLaterThanFromValidator,
          realizedToOrFromOnlyValidator,
          recurrenceUntilLaterThanPlannedFromValidator,
          recurrenceUntilLaterThan1970,
          recurrentAssistanceHasAtLeastOneOccurrence(this.assistance),
          validateRequiredFieldsWhenFormDisabled,
        ],
        asyncValidators: [
          isPlannedDateTimeSoonerThanConfigAllowsAsyncValidator(this.assistance, this.configService),
          isLateOrderedAndRecurrentAsyncValidator(),
          isOverlappingAnyOtherAssistanceForTheSameClientAsyncValidator(this.assistanceService, this),
          isOverlappingAnyOtherAssistanceForTheSameAssistantAsyncValidator(this.assistanceService, this.assistance),
        ],
      },
    )
    this.initialJournalEntries = this.form.get('journalEntries').value ?? []

    this.changeHandler = new EditorAssistanceFormValueChangeHandler(
      this,
      await firstValueFrom(this.statusService.getAll()),
    )
    this.changeHandler.handleInitial()
    this.lateOrderCheck()

    this.subscribeFormChanges()
  }

  subscribeFormChanges() {
    // TODO: this should be refactored away
    this.recurrenceEditorChange
      .pipe(
        tap((event) => this.forceDisableAssistantField.setState('recurrent', event.value !== '')),
        // debounceTime(200),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((event) => {
        // We setTimeout because we need to ensure that this happens after click handler on recurrence editor has been fired
        setTimeout(() => {
          if (this.recurrenceEditorWasClickedAtLeastOnce) {
            // TODO: Move to change handler
            // Reason for this is that recurrent assistances should not be able to have recurrent children
            this.assistanceEditorModalService.setEditorPreventAddBundleChild(event.value !== '')
          }
        })
      })

    // Subscribe to clientId changes to reload tag editor tree nodes
    this.form
      .get('clientId')
      .valueChanges.pipe(distinctUntilChanged(), debounceTime(300), takeUntil(this.unsubscribe$))
      .subscribe((newClientId: number) => {
        if (newClientId) {
          this.clientId = newClientId
        }
      })

    this.changeHandler.setValueChangesHandlers()

    /**
     *  Monitors changes in the form and updates form status accordingly. Checks whether the form is valid and if there are any unsaved changes, submits the form.
     */
    this.form.valueChanges
      .pipe(
        tap((formChanges) => {
          console.log(this.TAG, 'form valueChanges', formChanges, this.form.status)
        }),
        tap((formChanges) => {
          const current = this.form.get('journalEntries').value ?? []
          if (
            formChanges.journalEntries &&
            JSON.stringify(this.initialJournalEntries) === JSON.stringify(current)
          ) {
            this.form.get('journalEntries').markAsPristine()
          }
          console.log(this.TAG, 'form valueChanges', formChanges, FormsUtilsService.getChangedProperties(this.form))
          console.log(this.TAG, 'is form dirty', this.form.dirty)
        }),
        filter((_) => this.form.dirty),
        tap((formChanges) => {
          if (FeatureFlags.assistanceEditor_autosave) this.assistanceEditorModalService.formStatus.setSaving()
        }),
        switchMap((_) => {
          // This block waits for the form to resolve its status and then continue the rxjs stream.
          // The form is in PENDING state while its validations are running, but resolves into valid or invalid eventually.
          return this.form.statusChanges.pipe(
            startWith(this.form.status),
            tap((status) => {
              if (status === 'PENDING') this.assistanceEditorModalService.formStatus.setValidating()
            }),
            tap((status) => console.log(status)),
            filter((status) => status !== 'PENDING'),
            first(),
          )
        }),
        map(() => this.updateFormStatusBasedOnValidation()),
        takeUntil(this.unsubscribe$),
        debounceTime(800),
      )
      .subscribe(async (form) => {
        const changedProps = FormsUtilsService.getChangedProperties(form)
        console.log(this.TAG, 'changedProperties', changedProps)
        console.log(this.TAG, 'formStatus before submit', this.assistanceEditorModalService.formStatus.statusType)
        console.log(this.TAG, 'form errors before submit', this.form.errors)
        if (changedProps.hasOwnProperty('plannedFrom')) this.lateOrderCheck()
        if (
          this.assistanceEditorModalService.formStatus.is(FormStatusType.VALID_UNSAVED_CHANGES) &&
          FeatureFlags.assistanceEditor_autosave
        ) {
          await this.doSubmit()
        }
      })
  }
  private updateFormStatusBasedOnValidation() {
    console.log(this.TAG, 'is form valid?', this.form.status)
    console.log(this.TAG, 'is form enabled?', this.form.enabled)
    this.isWarningForRealizedDatesCache = this.isWarningForRealizedDates()

    if (this.form.valid) {
      this.realizedValidationErrorsMessages = []
      if (this.form.dirty && FormsUtilsService.getChangedProperties(this.form)) {
        this.assistanceEditorModalService.formStatus.setValidWithUnsavedChanges()
      } else {
        this.assistanceEditorModalService.formStatus.setSaved()
      }
    } else {
      const requiredLabels: string[] = []
      this.collectMissingRequiredFieldsLabels(requiredLabels)
      this.realizedValidationErrorsMessages = []
      this.collectRealizedValidationErrors(this.realizedFromToErrorMessages)
      this.assistanceEditorModalService.formStatus.setInvalid(!!this.assistance.id, requiredLabels)
    }

    return this.form
  }
  private collectMissingRequiredFieldsLabels(requiredLabels: string[]) {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key)
      if (control?.errors?.required) {
        if (key === 'clientId') {
          requiredLabels.push('Klient')
        } else if (this[key] && this[key].el.childNodes[0].data) {
          requiredLabels.push(this[key].el.childNodes[0].data.trim())
        }
      }
    })
    return requiredLabels
  }
  private collectRealizedValidationErrors(errorMessages) {
    if (this.form.errors) {
      this.areRealizedDatesValid = false
      Object.keys(this.form.errors).forEach((errorKey) => {
        const message = errorMessages[errorKey]
        if (message) {
          this.realizedValidationErrorsMessages.push(message)
        }
      })
    }
  }

  private async enrichJournalEntriesForDisplay(assistance: EditorAssistance): Promise<void> {
    const entries = assistance.journalEntries
    if (!entries?.length) return

    const enriched = await Promise.all(
      entries.map(async (entry: AssistanceJournalEntry): Promise<{ assistantId: number; content: string } | null> => {
        if (entry.assistantId) {
          return { assistantId: entry.assistantId, content: entry.content ?? '' }
        }
        if (entry.authorUserId) {
          const assistantId = await firstValueFrom(
            this.assistantService.getAssistantIdByUserId(entry.authorUserId),
          )
          return { assistantId, content: entry.content ?? '' }
        }
        return null
      }),
    )
    assistance.journalEntries = enriched.filter(Boolean) as { assistantId: number; content: string }[]
  }

  private async enrichJournalEntriesForPatch(assistance: Partial<EditorAssistance>): Promise<void> {
    const entries = assistance.journalEntries
    if (!entries?.length) return

    const enriched = await Promise.all(
      entries.map(
        async (
          entry: AssistanceJournalEntry & { authorUserId?: number },
        ): Promise<{ assistantId: number; content: string } | null> => {
          if (entry.assistantId) {
            return { assistantId: entry.assistantId, content: entry.content ?? '' }
          }
          if (entry.authorUserId) {
            const assistantId = await firstValueFrom(
              this.assistantService.getAssistantIdByUserId(entry.authorUserId),
            )
            return { assistantId, content: entry.content ?? '' }
          }
          return null
        },
      ),
    )
    assistance.journalEntries = enriched.filter(Boolean) as { assistantId: number; content: string }[]
  }

  private async doSubmit() {
    if (!this.form) return
    // const assistanceFromForm = this.sanitizeAssistanceData(this.form)
    console.log(this.TAG, 'doSubmit isCreateMode?', this.assistance.isNotSavedInDatabase())

    if (this.assistanceEditorModalService.formStatus.isSaving()) {
      console.warn('currently saving')
      return
    } else {
      this.assistanceEditorModalService.formStatus.setSaving()

      try {
        let record: EditorAssistance
        const assistanceWasRecurrentBeforeUpdate = !!this.assistance.rrule

        if (!this.assistance.id) {
          const assistance = mapFormFieldsToAssistance(this.form.value, this.form.value.urid)
          await this.enrichJournalEntriesForPatch(assistance)
          console.log(`${this.TAG}#doSubmit creating`, assistance)
          record = await this.assistanceEditorService.create(assistance, this.isSplitSeries)
          this.notifyURIDChange.emit(record.URID)
        } else {
          const changedProps = FormsUtilsService.getChangedProperties(this.form)
          const assistance = mapFormFieldsToAssistance(changedProps, this.form.value.urid)
          await this.enrichJournalEntriesForPatch(assistance)
          console.log(`${this.TAG}#doSubmit patching`, changedProps, assistance, this.form.value.urid)
          this.notifyURIDChange.emit(this.form.value.urid)
          record = await this.assistanceEditorService.patch(assistance)
        }

        console.log(this.TAG, 'new record after hitting server', record)
        if (record.rrule && !assistanceWasRecurrentBeforeUpdate) {
          const recordClient = await firstValueFrom(this.clientsService.getById(record.clientId))
          updateForm(this.form, record, recordClient)
          console.log(
            this.TAG,
            'Assistance became recurrent, switching recurrence edit mode to "current and following"',
          )
          this.changeRecurrenceEditingMode(RecurrenceEditingMode.currentOccurrenceAndFollowing)
        } else if (!record.rrule && assistanceWasRecurrentBeforeUpdate) {
          console.log(this.TAG, 'Assistance became non-recurrent, switching recurrence edit mode to "none"')
          this.changeRecurrenceEditingMode(RecurrenceEditingMode.none)
        }

        // TODO if just made an exception, change recurrence editing mode to "none"

        this.assistance = this.assistance.updateFromProps(record).shallowClone()
        this.assistance.journalEntries = this.form.get('journalEntries').value ?? []
        this.initialJournalEntries = this.form.get('journalEntries').value ?? []
        this.statusAllowsDeletion = await this.assistanceEditorService.canDeleteAssistance(this.assistance.statusId)
        this.assistanceEditorModalService.formStatus.setSaved()
        // this.lockService.unlock(this.assistance)
      } catch (e) {
        console.error(`${this.TAG}#doSubmit`, e, this.form.value, this.assistance)
        this.updateFormStatusBasedOnValidation()
        await this.initForm()
      } finally {
        console.log(`${this.TAG}#doSubmit markAsPristine`)
        this.form.markAsPristine()
      }

      console.log(`${this.TAG}#doSubmit end`)
      return
    }
  }

  public async deleteWithConfirmation() {
    const alert = await this.alertCtrl.create({
      header: 'Opravdu smazat?',
      message: 'Opravdu si přejete smazat tuto asistenci?',
      buttons: [
        {
          text: 'Zrušit',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Smazat',
          cssClass: ['primary', 'cyConfirmDeleteBtn'],
          handler: async () => {
            await this.assistanceEditorService.remove(this.assistance)
            console.log(this.TAG, 'emitting deleted')

            if (!this.assistance.isBundleChild()) {
              this.emitMainAssistanceDeleted()
            }
            if (this.assistance.isBundleChild()) {
              if (!this.assistance.id) throw new Error('Missing id on bundle child assistance')
              console.log(this.TAG, 'emitting bundleChildAssistanceDeleted')
              this.bundleChildAssistanceDeleted.emit(this.assistance.id)
            }
          },
        },
      ],
    })

    await alert.present().then(() => {
      const firstInput: any = document.querySelector('.alert-button.primary')
      firstInput.focus()
      return
    })
  }

  private emitMainAssistanceDeleted() {
    console.log(this.TAG, 'emitting mainAssistanceDeleted')
    this.assistanceEditorModalService.mainAssistanceDeletedInCurrentlyOpenEditor()
  }

  async createTemplate() {
    await this.assistanceEditorService.tryCreateTemplate(this.assistance)
  }

  async duplicate() {
    const statuses = await firstValueFrom(this.assistanceStatusManager.getStatuses())
    const clone = this.assistance.deepClone()

    clone.id = undefined
    clone.statusId = statuses.find((s) => s.name === AssistanceStatusName.PLANNED).id
    clone.assignments = []
    clone.isSentToClient = false
    clone.isShowInExchange = false
    clone.realizedFrom = null
    clone.realizedTo = null
    clone.recurrenceParentId = null
    clone.recurrenceException = null
    clone.recurrenceEnd = null
    clone.rrule = null

    await this.assistanceEditorModalService.closeAllModals()
    await this.assistanceEditorModalService.openEditor(clone)
  }

  ngOnDestroy(): void {
    this.unsubscribe$$.next()
    this.unsubscribe$$.complete()
    this.destroy$.next()
    this.destroy$.complete()
  }

  isAssistanceSaved(): boolean {
    return (
      (this.assistance.id || this.assistance.recurrenceParentId) &&
      [FormStatusType.NO_CHANGES, FormStatusType.SAVED].includes(
        this.assistanceEditorModalService.formStatus.statusType,
      )
    )
  }

  isAssistanceDuplicable(): boolean {
    return this.isAssistanceSaved() && !this.isInBundle && !Assistance.isRecurrent(this.assistance)
  }

  canShowDeleteButton() {
    return (this.assistance.isSavedInDatabase() || this.assistance.isBundleChild()) && this.statusAllowsDeletion
  }

  isAssistantSelectDisabled() {
    if (this.forceDisableAssistantField.getState()) return true
    const res =
      !this.assistance.clientId ||
      this.assistance.realizedFrom ||
      this.assistance.realizedTo ||
      // this.assistance.status.isNegative() || // TODO: Ask the status component
      (this.assistance.isNotSavedInDatabase() && this.recurrenceEditingMode === RecurrenceEditingMode.none) ||
      (this.assistance.isRecurrentButNotException() &&
        this.recurrenceEditingMode !== RecurrenceEditingMode.currentOccurrence)
    return !!res
  }

  getAssistantSelectFilterOverrides() {
    return {
      needsStrongAssistant: this.form.value.strongAssistantNeeded,
      needsAdvancedAssistant: this.form.value.advancedAssistantNeeded,
    }
  }

  getAssistanceRangeFromForm(): DateRange {
    const from = this.form.value.plannedFrom?.toDate(null)
    const to = this.form.value.plannedTo?.toDate(null)
    if (from && to) {
      return { from, to }
    }
  }

  fillRealizedInputAccordingToPlannedValue(realized, planned) {
    this.form.controls[realized].markAsDirty()
    this.form.get(realized).patchValue(DateTime.fromDate(this.assistance[planned]))
    this.isWarningForRealizedDatesCache = this.isWarningForRealizedDates()
  }

  // TODO: should be also in service, argument formstatus, should also prevent switching to another A in bundle unless saved
  async saveWithPopup() {
    console.log('saveWithPopup', this.assistanceEditorModalService.formStatus, AssistanceEditorSavePopupComponent.name)

    const modal = await this.modalCtrl.create({
      component: AssistanceEditorSavePopupComponent,
      cssClass: ['assistanceEditorSavePopupModal', 'auto-height'],
      componentProps: {
        statusChanges$: this.assistanceEditorModalService.formStatus.getObservable(),
      },
      id: AssistanceEditorSavePopupComponent.name,
    })

    modal.onDidDismiss().then((data) => {
      console.log('save popup dismiss', data.data)
      if (data.data?.success) {
        console.log('will tryClose')
        this.assistanceEditorModalService.closeAllModals()
      }
    })

    await modal.present()

    await this.doSubmit()
  }

  /**
   * Saves unsaved changes if needed before status change.
   * Only saves if assistance exists in database and has unsaved changes.
   */
  private async saveUnsavedChangesIfNeeded(): Promise<void> {
    if (
      this.assistanceEditorModalService.formStatus.statusType === FormStatusType.VALID_UNSAVED_CHANGES &&
      this.assistance.isSavedInDatabase()
    ) {
      await this.doSubmit()

      if (this.assistanceEditorModalService.formStatus.isSaving()) {
        await firstValueFrom(
          this.assistanceEditorModalService.formStatus.status$.pipe(
            filter((status) => !status.isSaving()),
            first(),
          ),
        )
      }
    }
  }

  async doAdvanceStatus() {
    await this.saveUnsavedChangesIfNeeded()
    await this.assignmentManager.advanceAssistanceStatus(this.assistance.id)
  }

  async cancelAssistanceWithConfirmation() {
    let message = 'Opravdu si přejete zrušit tuto asistenci?'
    if (await this.assistanceStatusManager.isLateToCancelAssistance(this.assistance)) {
      message = 'Asistence bude zrušená pozdě. '.concat(message)
    }

    const alert = await this.alertCtrl.create({
      header: 'Opravdu zrušit?',
      message,
      buttons: [
        {
          text: 'Nechci',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {},
        },
        {
          text: 'Ano, zrušit asistenci',
          cssClass: 'primary',
          handler: async () => {
            await this.saveUnsavedChangesIfNeeded()
            await this.assignmentManager.cancelAssistance(this.assistance.id)
          },
        },
      ],
    })
    await alert.present().then(() => {
      const firstInput: any = document.querySelector('.alert-button.primary')
      firstInput.focus()
      return
    })
  }

  uridChangeReportedFromAssistantSelect(assistanceURID: string) {
    console.log('uridChangeReportedFromAssistantSelect', assistanceURID)
    this.notifyURIDChange.emit(assistanceURID)
  }

  protected get sortedAssignments() {
    return this.assistance?.assignments?.sort((a, b) => a.id - b.id)
  }

  protected readonly FeatureFlags = FeatureFlags

  isWarningForRealizedDates(): boolean {
    if (this.form) {
      this.areRealizedDatesValid = true
      if (this.form.controls.realizedFrom.enabled && this.form.controls.realizedTo.enabled) {
        const realizedTo = this.form.controls.realizedTo.value
        const realizedFrom = this.form.controls.realizedFrom.value

        const isRealizedToComplete = !!(realizedTo?.date && realizedTo?.time)
        const isRealizedFromComplete = !!(realizedFrom?.date && realizedFrom?.time)

        if (isRealizedToComplete && isRealizedFromComplete) {
          return false
        } else if (isRealizedFromComplete || isRealizedToComplete) {
          return true
        }
      }
    }
  }

  private lateOrderCheck() {
    if (!this.form) return
    const isNew = !this.assistance.id
    const isRecurrent = Assistance.isRecurrent(this.assistance)

    if (isNew && !isRecurrent) {
      const plannedFrom = this.form.controls.plannedFrom.value?.toDate?.()

      if (plannedFrom && !this.form.controls.isLateOrder.dirty) {
        AssistanceHooks.isAssistanceLateOrdered(plannedFrom, this.usersService, this.configService).then((isLate) => {
          this.form.controls.isLateOrder.patchValue(isLate, { emitEvent: false })
        })
      }
    }
  }

  /**
   * Updates clientId when form changes (for tag editor)
   */
  private updateClientIdFromForm(): void {
    const currentClientId = this.form.get('clientId')?.value
    if (currentClientId) {
      this.clientId = currentClientId
    }
  }

  // Avoiding the RuntimeError: NG0100: ExpressionChangedAfterItHasBeenCheckedError
  get humanizedRecurrenceEditingMode(): string {
    return this.recurrenceEditingMode
      ? this.humanizeRecurrenceEditingModePipe.transform(this.recurrenceEditingMode)
      : null
  }
}
